// @ts-check

import { html } from "lit-html";

/** @typedef {ReturnType<typeof html>} LitView */

export const FIELD_CLASS =
  "h-11 w-full rounded-lg border border-stone-300 bg-white px-4 text-base text-stone-900 outline-none transition focus:border-stone-500 focus:ring-2 focus:ring-stone-400/20";
export const TEXTAREA_CLASS =
  "min-h-72 w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-base leading-7 text-stone-900 outline-none transition focus:border-stone-500 focus:ring-2 focus:ring-stone-400/20";
export const PRIMARY_BUTTON_CLASS =
  "inline-flex h-11 items-center justify-center rounded-lg bg-stone-900 px-4 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400";
export const SECONDARY_BUTTON_CLASS =
  "inline-flex h-11 items-center justify-center rounded-lg border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 hover:border-stone-400 disabled:cursor-not-allowed disabled:border-stone-200 disabled:bg-white disabled:text-stone-300";

/**
 * @param {string} title
 * @param {string} description
 * @param {LitView | LitView[] | string} body
 * @param {string} [className]
 * @returns {LitView}
 */
export function renderSectionCard(title, description, body, className) {
  const sectionClass = `grid min-w-0 gap-5 ${className || ""}`;

  return html`
    <section class=${sectionClass}>
      <h2
        class="border-b border-stone-100 pb-2.5 text-xs font-semibold uppercase tracking-widest text-stone-500"
      >
        ${title}
      </h2>
      ${body}
    </section>
  `;
}

/**
 * @param {string} text
 * @returns {LitView}
 */
export function renderPlaceholder(text) {
  return html` <div class="py-3 text-sm text-stone-400">${text}</div> `;
}

/**
 * @param {string} text
 * @returns {LitView}
 */
export function renderErrorMessage(text) {
  return html`
    <div
      class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
    >
      ${text}
    </div>
  `;
}

/**
 * @param {string} label
 * @param {string} value
 * @param {string} [valueClass]
 * @param {boolean} [withDivider]
 * @returns {LitView}
 */
export function renderDetailRow(label, value, valueClass, withDivider) {
  const detailValueClass = valueClass || "text-stone-900";
  const dividerClass = withDivider ? "border-t border-stone-200 pt-2" : "";
  const labelClass = `text-sm text-stone-500 ${dividerClass}`;
  const valueRowClass = `text-sm font-semibold ${detailValueClass} ${dividerClass}`;

  return html`
    <div class=${labelClass}>${label}</div>
    <div class=${valueRowClass}>${value}</div>
  `;
}

/**
 * @param {{
 *   label: string,
 *   value: string,
 *   onInput: (event: Event) => void,
 *   placeholder?: string,
 *   help?: string,
 *   disabled?: boolean,
 *   type?: string,
 *   step?: string,
 *   inputMode?: string,
 * }} options
 * @returns {LitView}
 */
export function renderInputField(options) {
  const inputType = options.type || "number";
  const inputStep = options.step || "any";
  const isDisabled = Boolean(options.disabled);

  return html`
    <label class="grid min-w-0 gap-1.5 text-sm font-medium text-stone-700">
      <span>${options.label}</span>
      <input
        class=${FIELD_CLASS}
        type=${inputType}
        step=${inputStep}
        inputmode=${options.inputMode || ""}
        .value=${options.value}
        placeholder=${options.placeholder || ""}
        ?disabled=${isDisabled}
        @input=${options.onInput}
      />
      ${options.help
        ? html`<span class="text-xs font-normal leading-5 text-stone-400"
            >${options.help}</span
          >`
        : ""}
    </label>
  `;
}

/**
 * @param {{
 *   label: string,
 *   value: string,
 *   onInput: (event: Event) => void,
 *   onStepDown: () => void,
 *   onStepUp: () => void,
 *   placeholder?: string,
 *   help?: string,
 *   disabled?: boolean,
 * }} options
 * @returns {LitView}
 */
export function renderSteppedNumberField(options) {
  const isDisabled = Boolean(options.disabled);

  return html`
    <label class="grid min-w-0 gap-1.5 text-sm font-medium text-stone-700">
      <span>${options.label}</span>
      <span class="grid min-w-0 grid-cols-[minmax(0,1fr)_2.75rem_2.75rem] gap-1.5">
        <input
          class=${FIELD_CLASS}
          type="text"
          inputmode="numeric"
          .value=${options.value}
          placeholder=${options.placeholder || ""}
          ?disabled=${isDisabled}
          @input=${options.onInput}
        />
        <button
          class="inline-flex h-11 items-center justify-center rounded-lg border border-stone-300 bg-white text-base font-semibold text-stone-700 transition hover:border-stone-400 hover:bg-stone-50 disabled:cursor-not-allowed disabled:border-stone-200 disabled:text-stone-300"
          type="button"
          aria-label="Increase ${options.label}"
          ?disabled=${isDisabled}
          @click=${options.onStepUp}
        >
          ${renderArrowUpIcon()}
        </button>
        <button
          class="inline-flex h-11 items-center justify-center rounded-lg border border-stone-300 bg-white text-base font-semibold text-stone-700 transition hover:border-stone-400 hover:bg-stone-50 disabled:cursor-not-allowed disabled:border-stone-200 disabled:text-stone-300"
          type="button"
          aria-label="Decrease ${options.label}"
          ?disabled=${isDisabled}
          @click=${options.onStepDown}
        >
          ${renderArrowDownIcon()}
        </button>
      </span>
      ${options.help
        ? html`<span class="text-xs font-normal leading-5 text-stone-400"
            >${options.help}</span
          >`
        : ""}
    </label>
  `;
}

/**
 * @param {{
 *   label: string,
 *   value: string,
 *   onInput: (event: Event) => void,
 *   placeholder?: string,
 *   help?: string,
 * }} options
 * @returns {LitView}
 */
export function renderTextareaField(options) {
  return html`
    <label class="grid gap-1.5 text-sm font-medium text-stone-700">
      <span>${options.label}</span>
      <textarea
        class=${TEXTAREA_CLASS}
        .value=${options.value}
        placeholder=${options.placeholder || ""}
        @input=${options.onInput}
      ></textarea>
      ${options.help
        ? html`<span class="text-xs font-normal leading-5 text-stone-400"
            >${options.help}</span
          >`
        : ""}
    </label>
  `;
}

/**
 * @param {{
 *   label: string,
 *   value: string,
 *   onInput: (event: Event) => void,
 *   options: Array<{ value: string, label: string }>,
 *   help?: string,
 *   disabled?: boolean,
 * }} options
 * @returns {LitView}
 */
export function renderSelectField(options) {
  /** @type {LitView[]} */
  const optionViews = [];
  for (let i = 0, total = options.options.length; i < total; i += 1) {
    const option = options.options[i];
    optionViews.push(html`
      <option value=${option.value}>${option.label}</option>
    `);
  }

  return html`
    <label class="grid gap-1.5 text-sm font-medium text-stone-700">
      <span>${options.label}</span>
      <select
        class=${FIELD_CLASS}
        .value=${options.value}
        ?disabled=${Boolean(options.disabled)}
        @input=${options.onInput}
      >
        ${optionViews}
      </select>
      ${options.help
        ? html`<span class="text-xs font-normal leading-5 text-stone-400"
            >${options.help}</span
          >`
        : ""}
    </label>
  `;
}

/**
 * @param {{
 *   label: string,
 *   checked: boolean,
 *   onInput: (event: Event) => void,
 *   help?: string,
 * }} options
 * @returns {LitView}
 */
export function renderCheckboxField(options) {
  return html`
    <label
      class="flex items-start gap-3 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800"
    >
      <input
        class="mt-0.5 h-4 w-4 rounded border-stone-400 bg-white text-stone-900 focus:ring-stone-400/30"
        type="checkbox"
        ?checked=${options.checked}
        @input=${options.onInput}
      />
      <span>
        <span class="block font-medium text-stone-900">${options.label}</span>
        ${options.help
          ? html`<span class="mt-1 block text-xs leading-5 text-stone-400"
              >${options.help}</span
            >`
          : ""}
      </span>
    </label>
  `;
}

/**
 * @returns {LitView}
 */
function renderArrowDownIcon() {
  return html`
    <svg
      class="size-5"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fill-rule="evenodd"
        d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
        clip-rule="evenodd"
      />
    </svg>
  `;
}

/**
 * @returns {LitView}
 */
function renderArrowUpIcon() {
  return html`
    <svg
      class="size-5"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fill-rule="evenodd"
        d="M14.78 11.78a.75.75 0 0 1-1.06 0L10 8.06l-3.72 3.72a.75.75 0 1 1-1.06-1.06l4.25-4.25a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06Z"
        clip-rule="evenodd"
      />
    </svg>
  `;
}

/**
 * @param {string} value
 * @param {string} label
 * @param {string} className
 * @returns {LitView}
 */
export function renderMetricTile(value, label, className) {
  return html`
    <section class="rounded-lg bg-stone-100 p-5">
      <div class="text-3xl font-semibold tracking-tight text-stone-900">
        ${value}
      </div>
      <div class="mt-2 text-sm font-medium text-stone-500">${label}</div>
    </section>
  `;
}

/**
 * @param {string} label
 * @param {string} value
 * @returns {LitView}
 */
export function renderTimeResultBlock(label, value) {
  return html`
    <div class="border-b border-stone-100 py-3">
      <div
        class="text-xs font-semibold uppercase tracking-widest text-stone-400"
      >
        ${label}
      </div>
      <div class="mt-1 text-sm text-stone-800">${value}</div>
    </div>
  `;
}
